import React, { useState } from 'react';
import styles from './InputComponents.module.css';

const TableInput = ({ question, value, onUpdate, shouldShow, isSubmitted }) => {
    // Initialize all hooks first (before any conditional returns)
    const [showModal, setShowModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [formData, setFormData] = useState({});
    const [tableData, setTableData] = useState(value || []);

    // Conditional return after hooks
    if (!shouldShow) return null;

    const tableConfig = question.questions;

    const handleAdd = () => {
        setFormData({});
        setEditingIndex(null);
        setShowModal(true);
    };

    const handleEdit = (index) => {
        setFormData(tableData[index]);
        setEditingIndex(index);
        setShowModal(true);
    };

    const handleDelete = (index) => {
        const message = tableConfig.actions?.deleteAlertMessage || 'Are you sure you want to delete this record?';
        if (window.confirm(message)) {
            const newData = tableData.filter((_, i) => i !== index);
            setTableData(newData);
            onUpdate({ [question.id]: newData });
        }
    };

    const handleSave = () => {
        let newData;
        if (editingIndex !== null) {
            // Edit existing
            newData = [...tableData];
            newData[editingIndex] = {
                ...formData,
                annotationNumber: editingIndex + 1
            };
        } else {
            // Add new
            newData = [...tableData, {
                ...formData,
                annotationNumber: tableData.length + 1
            }];
        }
        setTableData(newData);
        onUpdate({ [question.id]: newData });
        setShowModal(false);
        setFormData({});
    };

    const handleCancel = () => {
        setShowModal(false);
        setFormData({});
        setEditingIndex(null);
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData({ ...formData, [fieldId]: value });
    };

    const renderFormField = (column) => {
        if (column.type === 'auto') return null;

        const value = formData[column.id] || '';

        switch (column.type) {
            case 'text':
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleFieldChange(column.id, e.target.value)}
                        placeholder={column.placeholder}
                        className={styles.input}
                        required={column.required}
                    />
                );

            case 'select':
                return (
                    <select
                        value={value}
                        onChange={(e) => handleFieldChange(column.id, e.target.value)}
                        className={styles.select}
                        required={column.required}
                    >
                        <option value="">-- Select --</option>
                        {column.options?.map((opt, idx) => (
                            <option key={idx} value={opt}>{opt}</option>
                        ))}
                    </select>
                );

            default:
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleFieldChange(column.id, e.target.value)}
                        className={styles.input}
                    />
                );
        }
    };

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h4>{tableConfig.title || question.text}</h4>
                {!isSubmitted && tableConfig.actions?.create && (
                    <button
                        type="button"
                        onClick={handleAdd}
                        className={styles.addButton}
                    >
                        + Add New
                    </button>
                )}
            </div>

            {tableData.length > 0 ? (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {tableConfig.columns?.map((col, idx) => (
                                    col.visible !== false && (
                                        <th key={idx}>{col.label}</th>
                                    )
                                ))}
                                {!isSubmitted && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {tableConfig.columns?.map((col, colIndex) => (
                                        col.visible !== false && (
                                            <td key={colIndex}>
                                                {col.type === 'auto'
                                                    ? row.annotationNumber
                                                    : row[col.id] || '-'}
                                            </td>
                                        )
                                    ))}
                                    {!isSubmitted && (
                                        <td>
                                            <div className={styles.actionButtons}>
                                                {tableConfig.actions?.edit && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(rowIndex)}
                                                        className={styles.editButton}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {tableConfig.actions?.delete && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(rowIndex)}
                                                        className={styles.deleteButton}
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <p>No records added yet.</p>
                </div>
            )}

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>{tableConfig.popupForm?.title || 'Add / Edit Record'}</h3>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {tableConfig.popupForm?.fields.map((fieldId) => {
                                const column = tableConfig.columns?.find(col => col.id === fieldId);
                                if (!column) return null;

                                return (
                                    <div key={fieldId} className={styles.formField}>
                                        <label className={styles.fieldLabel}>
                                            {column.label}
                                            {column.required && <span className={styles.required}>*</span>}
                                        </label>
                                        {renderFormField(column)}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                onClick={handleSave}
                                className={styles.saveButton}
                            >
                                {tableConfig.popupForm?.buttons?.save || 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={handleCancel}
                                className={styles.cancelButton}
                            >
                                {tableConfig.popupForm?.buttons?.cancel || 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableInput;